/* eslint-disable @typescript-eslint/unbound-method */
import React, {
  CSSProperties,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ContentLocation, PopoverState } from 'react-tiny-popover';
import ConfirmActionPopover, {
  PopoverConfirmProps,
} from '../Popovers/ConfirmActionPopover';
import CommentCheckIcon from './CommentCheckIcon';
import { useTranslation } from 'react-i18next';
import Can from '../../../components/Can';
import { g as abilityGlossary } from '../../../config/ability';
import RichTextEditor from '../ActivityFeed/CommentForm/RichTextEditor';
import { Descendant } from 'slate';
import './BillPosition.scss';
import { SubmitHandler, useForm } from 'react-hook-form';
import { BillAPI } from '../../../api';
import { Bill } from '@enview/interface/types/bills/Bill';
import { PositionType } from '@enview/interface/types/bills/PositionStatement';
import { mobileMediaQuery } from '../../../helpers/mediaQueries';
import {
  isElementEmpty,
  serializeToPlainText,
  serializeToReactElement,
} from '@enview/interface/utils/RichTextAnnotation';
import sanitizeHtml from 'sanitize-html';
import { UserAction } from '@enview/interface/types/actions/UserAction';
import { useSelector } from 'react-redux';
import { State } from '../../../dux/@types';
import { CreatePositionDto } from '../../../api/bill';
import {
  trackBillPositionAdded,
  trackBillPositionDeleted,
  trackBillPositionUpdated,
} from '../../../analytics/BillAnalytics';
import SwitchIcon from '../Icons/SwitchIcon';
import { Trans } from 'react-i18next';
import SingleSelectDropDown, { OptionType } from '../DropDowns/SingleSelectDropDown';
import { getPositionColor } from './PositionColors';
interface PositionStatementPopoverProps extends Partial<PopoverConfirmProps> {
  bill: Bill;
  target: ReactElement;
  show: boolean;
  toggleCallback: (evt: React.SyntheticEvent) => void;
  closePopup: () => void;
  readOnly?: boolean;
}
type PositionFormInput = { positionType: string; isPublic: boolean };

const PLACEHOLDER_TEXT = 'Add an optional position statement';

const POSITION_OPTIONS: OptionType<PositionType>[] = [
  PositionType.MONITORING,
  PositionType.AMEND,
  PositionType.NEUTRAL,
  PositionType.DEFENSIVE,
  PositionType.PROACTIVE,
  PositionType.SUPPORT,
  PositionType.OPPOSE,
].map((pt) => ({ label: pt, value: pt }));

export const PositionStatementPopover: React.FC<PositionStatementPopoverProps> = ({
  bill,
  target,
  show,
  toggleCallback,
  closePopup,
  readOnly = false,
  ...props
}) => {
  const [editMode, setEditMode] = useState(false);
  const [comment, setComment] = useState(PLACEHOLDER_TEXT as any);
  const [hasStatement, setHasStatement] = useState(true);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { t } = useTranslation();
  const nodeRef = useRef<any>();

  const [addPosition, { isLoading }] =
    BillAPI.endpoints.createPositionStatement.useMutation();
  const [updatePosition] = BillAPI.endpoints.updatePositionStatement.useMutation();
  const [deletePosition] = BillAPI.endpoints.deletePositionStatement.useMutation();

  const orgId = useSelector((state: State) => state.teamMode.orgId);
  const orgUser = useSelector((state: State) => state.account.organizationUser);

  const positionStatement = bill.positionStatements && bill.positionStatements[0];

  const positionColor = getPositionColor(positionStatement?.positionType);

  useEffect(() => {
    if (!positionStatement) {
      setHasStatement(false);
      if (!readOnly) setEditMode(true);
    } else {
      setHasStatement(true);
    }
  }, [positionStatement, readOnly]);

  const getComments = useMemo(() => {
    const statement: Partial<UserAction> = { comment: '' };
    const isEmptyText =
      typeof comment !== 'string' && serializeToPlainText({ children: comment }) === '';

    if (!isEmptyText && comment !== PLACEHOLDER_TEXT) {
      if (typeof comment === 'object') {
        statement.commentJson = { children: comment };
      } else {
        statement.comment = sanitizeHtml(comment.replace(/<div>/g, ' '), {
          allowedTags: [],
        });
      }
    }

    return statement;
  }, [comment]);

  const getUserAction = useMemo(() => {
    if (!positionStatement) return;
    const { statementJson, positionStatement: statementComment } = positionStatement;

    if (statementComment || statementJson) {
      return {
        comment: statementComment,
        commentJson: statementJson,
      } as Partial<UserAction>;
    }
  }, [positionStatement]);

  const getPositionType = (value: string): PositionType => {
    return (
      (Object.values(PositionType).find((pt) => pt === value) as PositionType) ??
      PositionType.NO_POSITION
    );
  };

  let positionComment = <></>;
  if (!!positionStatement && Object.keys(positionStatement).length > 0) {
    positionComment = positionStatement.statementJson ? (
      <span>
        {serializeToReactElement(positionStatement.statementJson, orgUser?.id)}
      </span>
    ) : (
      <span>{positionStatement.positionStatement}</span>
    );
  }

  const getDefaultPositionType = (): string => {
    return positionStatement?.positionType ?? '';
  };

  const getDefaultVisibility = () => {
    if (positionStatement && positionStatement.isPublic) return true;
    return false;
  };

  const { register, handleSubmit, setValue, watch } = useForm<PositionFormInput>({
    defaultValues: {
      positionType: getDefaultPositionType(),
      isPublic: getDefaultVisibility(),
    },
  });
  useEffect(() => {
    register('positionType');
    register('isPublic');
  }, []);

  const positionTypeValue = watch('positionType') as PositionType | undefined;
  const publishStatus = watch('isPublic');

  const onClosePopup = () => {
    setEditMode(!hasStatement);
    setConfirmingDelete(false);
    closePopup();
  };

  const onSubmit: SubmitHandler<PositionFormInput> = async (data) => {
    if (!orgId) {
      return;
    }

    const formData: CreatePositionDto = {
      positionType: getPositionType(data.positionType),
      isPublic: data.isPublic,
      positionStatement: getComments.comment || '',
      orgId,
      billId: bill.id,
    };

    if (getComments.commentJson) {
      formData.statementJson = getComments.commentJson;
    }

    // Do nothing if neither position stance nor comment is set
    if (
      !formData.positionStatement &&
      formData.positionType === PositionType.NO_POSITION &&
      !formData.statementJson
    ) {
      return;
    }

    const hasStance = formData.positionType !== PositionType.NO_POSITION;
    const hasComment = !!(formData.positionStatement || formData.statementJson);

    onClosePopup();
    setEditMode(false);

    if (positionStatement && hasStatement) {
      await updatePosition({
        id: positionStatement.id,
        updateData: formData,
      });
      trackBillPositionUpdated(
        bill.id,
        hasStance,
        hasComment,
        formData.isPublic || false,
      );
    } else {
      await addPosition(formData);
      trackBillPositionAdded(
        bill.id,
        hasStance,
        hasComment,
        formData.isPublic || false,
      );
    }
  };

  const onDeletePosition = async () => {
    if (!positionStatement) {
      return;
    }
    await deletePosition({
      id: positionStatement.id,
    });
    trackBillPositionDeleted(bill.id);

    onClosePopup();
  };

  const onCancel = () => {
    setEditMode(false);
    onClosePopup();
  };

  const onChangeToEditor = (event: Descendant[]) => {
    setComment(event);
  };

  const organization =
    orgUser && orgId
      ? orgUser.organizations.find((org) => org.id === orgId)
      : undefined;

  let popoverPositions = props.positions;
  if (!popoverPositions) {
    popoverPositions = mobileMediaQuery.matches ? ['top', 'bottom'] : ['bottom', 'top'];
  }

  const constrainedMode = useRef(false);
  const [contentStyle, setContentStyle] = useState<CSSProperties>({});

  useEffect(() => {
    if (!show) {
      constrainedMode.current = false;
      setContentStyle({});
    }
  }, [show]);

  const contentLocation = useCallback(
    ({ childRect, popoverRect, parentRect }: PopoverState): ContentLocation => {
      const PADDING = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const modalHeight = popoverRect.height;

      const spaceBelow = viewportHeight - childRect.bottom - PADDING;
      const spaceAbove = childRect.top - PADDING;

      const fitsBelow = modalHeight > 0 && modalHeight <= spaceBelow;
      const fitsAbove = modalHeight > 0 && modalHeight <= spaceAbove;

      let vy: number;
      if (fitsBelow && !constrainedMode.current) {
        vy = childRect.bottom + PADDING;
        setContentStyle((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      } else if (fitsAbove && !constrainedMode.current) {
        vy = childRect.top - modalHeight - PADDING;
        setContentStyle((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      } else if (modalHeight > 0) {
        constrainedMode.current = true;
        const maxSpace = Math.max(spaceBelow, spaceAbove);
        setContentStyle((prev) => {
          const targetMaxHeight = `${maxSpace}px`;
          if (prev.maxHeight === targetMaxHeight) return prev;
          return { maxHeight: targetMaxHeight, overflowY: 'auto' };
        });
        vy = spaceBelow >= spaceAbove ? childRect.bottom + PADDING : PADDING;
      } else {
        // modalHeight is 0 on first render — use default bottom position
        vy = childRect.bottom + PADDING;
      }

      let vx = childRect.left + childRect.width / 2 - popoverRect.width / 2;
      vx = Math.max(PADDING, Math.min(vx, viewportWidth - popoverRect.width - PADDING));

      return {
        top: vy - parentRect.top,
        left: vx - parentRect.left,
      };
    },
    [],
  );

  return (
    <ConfirmActionPopover
      {...props}
      closePopup={onClosePopup}
      content={
        editMode ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <h4 className="popover-header">
              <CommentCheckIcon className="mr-2" color={'#212529'} />
              {organization
                ? `${organization.name}'s position on this bill`
                : 'Position on Bill'}
            </h4>
            <p className="position-statement-subheader">
              {
                <Trans
                  components={{
                    hubLink: <a href={t('urls.positionStatement')} target="_blank" />,
                  }}
                  i18nKey="bill.sidebar.positionStatement.subHeaderText"
                  key="bill.sidebar.positionStatement.subHeaderText"
                />
              }
            </p>
            <div className="form-group mb-3">
              <SingleSelectDropDown
                alignRight={false}
                defaultLabel="Select a position..."
                onSelect={(option) => setValue('positionType', option.value)}
                options={POSITION_OPTIONS}
                selectedValue={positionTypeValue || undefined}
              />
            </div>
            <div className="rich-text-editor my-4" id="text-editor" ref={nodeRef}>
              <RichTextEditor
                onChangeToEditor={onChangeToEditor}
                placeholder={PLACEHOLDER_TEXT}
                userAction={getUserAction}
              />
            </div>
            <Can
              I={abilityGlossary.PUBLISH}
              a={abilityGlossary.BILL_POSITION}
              passThrough
            >
              {(can: boolean) =>
                can && (
                  <SwitchIcon
                    additionalExplanation={t(
                      `bill.sidebar.positionStatement.additionalExplanation`,
                    )}
                    disabled={!can}
                    id="isPublicCheckbox"
                    labelClassName="font-14"
                    offDescription={t(`bill.sidebar.positionStatement.publishOff`)}
                    onChange={(value) => {
                      setValue('isPublic', value);
                    }}
                    onDescription={t(`bill.sidebar.positionStatement.publishOn`)}
                    size="lg"
                    value={publishStatus}
                  />
                )
              }
            </Can>
            <div className="d-flex justify-content-start mt-3">
              <button className="btn primary-btn" disabled={isLoading} type="submit">
                Save
              </button>
              <button className="btn ml-3" onClick={() => onCancel()}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div>
            <h4 className="popover-header">
              <CommentCheckIcon className="mr-2" color={'#212529'} />
              {t('bill.sidebar.positionStatement.modalheader')}
            </h4>
            <div className="content-body my-3">
              {positionStatement &&
                positionStatement.positionType !== PositionType.NO_POSITION && (
                  <div
                    className="position-button mb-2"
                    style={{ backgroundColor: positionColor }}
                  >
                    {positionStatement?.positionType}
                  </div>
                )}
              {!isElementEmpty(positionComment) && (
                <div className="statement-box mt-4">{positionComment}</div>
              )}
            </div>
            {!readOnly && confirmingDelete && (
              <div className="content-body my-3">
                <p>
                  Are you sure you want to delete this position statement? This cannot
                  be undone.
                </p>
                <div className="d-flex justify-content-start footer">
                  <button className="btn delete-btn" onClick={onDeletePosition}>
                    Delete
                  </button>
                  <button
                    className="btn ml-3"
                    onClick={() => setConfirmingDelete(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {!readOnly && !confirmingDelete && (
              <div className="d-flex justify-content-start footer">
                <button className="btn primary-btn" onClick={() => setEditMode(true)}>
                  Edit
                </button>
                <button
                  className="btn delete-btn ml-3"
                  onClick={() => setConfirmingDelete(true)}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        )
      }
      contentLocation={contentLocation}
      contentStyle={contentStyle}
      disableReposition={false}
      positions={popoverPositions}
      show={show}
      style={editMode && !mobileMediaQuery.matches ? { minWidth: '500px' } : {}}
      target={target}
      toggleCallback={toggleCallback}
    />
  );
};

export default PositionStatementPopover;
